import React, { useEffect, useState } from "react";
import { X, Sparkles, Building2, Target, Wallet, CheckCircle2, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const INVESTOR_TYPES = ["Retail investor", "Angel investor", "HNI", "Family Office", "VC Fund", "PE Fund", "AIF", "NBFC", "Bank", "Strategic Investor", "Debt Fund", "Institutional Investor"];
const SECTORS = ["Fintech", "CleanTech", "HealthTech", "SaaS", "Consumer", "MSME", "Manufacturing", "Enterprise SaaS", "AgriTech", "EdTech", "Logistics"];
const STAGES = ["Idea", "Pre-Seed", "Seed", "Pre-Series A", "Series A", "Series B", "Growth"];
const GEOS = ["Bengaluru", "Mumbai", "Delhi NCR", "Pune", "Hyderabad", "Chennai", "India-wide", "Global"];

const STEPS = [
  { key: "welcome", label: "Welcome" },
  { key: "firm", label: "Firm" },
  { key: "thesis", label: "Thesis" },
  { key: "ticket", label: "Ticket" },
  { key: "done", label: "You're set" },
];

export default function InvestorOnboardingWizard({ open, onClose }) {
  const [idx, setIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    investor_name: "",
    investor_type: "",
    contact_person: "",
    mobile: "",
    city: "",
  });
  const [prefs, setPrefs] = useState({
    preferred_sectors: [],
    preferred_industries: [],
    preferred_stages: [],
    preferred_geography: [],
    excluded_sectors: [],
    ticket_size_min: "",
    ticket_size_max: "",
    risk_appetite: "",
  });

  useEffect(() => {
    if (!open) return;
    setIdx(0);
    api.get("/investor/me").then((r) => {
      const inv = r.data?.investor || {};
      const p = r.data?.preferences || {};
      setProfile({
        investor_name: inv.investor_name || "",
        investor_type: inv.investor_type || "",
        contact_person: inv.contact_person || "",
        mobile: inv.mobile || "",
        city: inv.city || "",
      });
      setPrefs({
        preferred_sectors: p.preferred_sectors || [],
        preferred_industries: p.preferred_industries || [],
        preferred_stages: p.preferred_stages || [],
        preferred_geography: p.preferred_geography || [],
        excluded_sectors: p.excluded_sectors || [],
        ticket_size_min: p.ticket_size_min || "",
        ticket_size_max: p.ticket_size_max || "",
        risk_appetite: p.risk_appetite || "",
      });
    }).catch(() => { /* silent */ });
  }, [open]);

  const setP = (k) => (e) => setProfile((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (key, val) => {
    setPrefs((p) => {
      const arr = p[key] || [];
      return { ...p, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  };

  const persistProfile = async (patch) => {
    setSaving(true);
    try { await api.put("/investor/profile", patch); }
    catch (err) { toast.error(formatApiError(err)); throw err; }
    finally { setSaving(false); }
  };

  const persistPrefs = async (patch) => {
    setSaving(true);
    try {
      // Preferences endpoint needs full model; merge current state with patch.
      const merged = {
        preferred_sectors: prefs.preferred_sectors,
        preferred_industries: prefs.preferred_industries,
        preferred_stages: prefs.preferred_stages,
        preferred_geography: prefs.preferred_geography,
        excluded_sectors: prefs.excluded_sectors,
        ticket_size_min: prefs.ticket_size_min ? Number(prefs.ticket_size_min) : null,
        ticket_size_max: prefs.ticket_size_max ? Number(prefs.ticket_size_max) : null,
        risk_appetite: prefs.risk_appetite || null,
        ...patch,
      };
      await api.put("/investor/preferences", merged);
    } catch (err) { toast.error(formatApiError(err)); throw err; }
    finally { setSaving(false); }
  };

  const dismiss = async () => {
    try { await api.post("/investor/onboarding/dismiss-welcome"); } catch (e) { /* silent */ }
    onClose?.();
  };

  const next = async () => {
    const step = STEPS[idx].key;
    try {
      if (step === "firm") {
        await persistProfile({
          investor_name: profile.investor_name || undefined,
          investor_type: profile.investor_type || undefined,
          contact_person: profile.contact_person || undefined,
          mobile: profile.mobile || undefined,
          city: profile.city || undefined,
        });
      } else if (step === "thesis") {
        await persistPrefs({});
      } else if (step === "ticket") {
        await persistPrefs({
          ticket_size_min: prefs.ticket_size_min ? Number(prefs.ticket_size_min) : null,
          ticket_size_max: prefs.ticket_size_max ? Number(prefs.ticket_size_max) : null,
          risk_appetite: prefs.risk_appetite || null,
        });
      }
    } catch (e) { return; }
    if (idx === STEPS.length - 1) { await dismiss(); return; }
    setIdx((i) => Math.min(STEPS.length - 1, i + 1));
  };
  const back = () => setIdx((i) => Math.max(0, i - 1));

  if (!open) return null;
  const step = STEPS[idx].key;
  const isLast = idx === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="investor-wizard"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-w-2xl w-full bg-white rounded-xl shadow-2xl border border-navy/10 overflow-hidden">
        {/* Header */}
        <div className="relative px-8 py-6 bg-gradient-to-br from-emerald-600 to-emerald-800 text-white">
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            data-testid="investor-wizard-skip"
            aria-label="Skip wizard"
          >
            <X size={20} />
          </button>
          <div className="overline text-white/70 mb-2">Investor Onboarding · Step {idx + 1} of {STEPS.length}</div>
          <div className="flex items-center gap-1.5" data-testid="investor-wizard-steps">
            {STEPS.map((s, i) => (
              <div
                key={s.key}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-10 bg-white" : i < idx ? "w-6 bg-white/70" : "w-4 bg-white/25"}`}
                aria-label={s.label}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-8 min-h-[280px]">
          {step === "welcome" && (
            <div data-testid="investor-wizard-step-welcome">
              <div className="flex items-center gap-2 mb-3 text-emerald-700">
                <Sparkles size={16} />
                <div className="overline">Welcome to VenturaZ</div>
              </div>
              <div className="font-heading text-2xl text-ink mb-3">Let{"\u2019"}s tune your deal flow.</div>
              <p className="text-ink-soft leading-relaxed mb-4">
                Four short screens · under 3 minutes. We use your thesis to curate startups that actually
                match your ticket range, sectors and stage preferences.
              </p>
              <ul className="space-y-2">
                {["Firm identity & contact", "Sector + stage thesis", "Ticket size + risk appetite"].map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-ink" data-testid={`investor-wizard-bullet-${i}`}>
                    <ArrowRight size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {step === "firm" && (
            <div data-testid="investor-wizard-step-firm">
              <div className="flex items-center gap-2 mb-3 text-emerald-700">
                <Building2 size={16} />
                <div className="overline">Firm</div>
              </div>
              <div className="font-heading text-xl text-ink mb-4">Tell us about your firm.</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Firm / Fund Name" value={profile.investor_name} onChange={setP("investor_name")} testId="investor-wizard-firm-name" placeholder="Northstar Capital" />
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Investor Type</div>
                  <select
                    value={profile.investor_type}
                    onChange={setP("investor_type")}
                    className="w-full px-3 py-2.5 rounded-md border border-navy/15 focus:border-emerald-500 outline-none text-ink bg-white"
                    data-testid="investor-wizard-type"
                  >
                    <option value="">Select type</option>
                    {INVESTOR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <Field label="Contact Person" value={profile.contact_person} onChange={setP("contact_person")} testId="investor-wizard-contact" />
                <Field label="Mobile" value={profile.mobile} onChange={setP("mobile")} testId="investor-wizard-mobile" placeholder="+91 98XXXXXXX" />
                <Field label="City" value={profile.city} onChange={setP("city")} testId="investor-wizard-city" />
              </div>
            </div>
          )}

          {step === "thesis" && (
            <div data-testid="investor-wizard-step-thesis">
              <div className="flex items-center gap-2 mb-3 text-emerald-700">
                <Target size={16} />
                <div className="overline">Thesis</div>
              </div>
              <div className="font-heading text-xl text-ink mb-4">Where do you play?</div>
              <div className="space-y-4">
                <ChipRow label="Preferred Sectors" values={SECTORS} selected={prefs.preferred_sectors} onToggle={(v) => toggle("preferred_sectors", v)} keyPrefix="investor-wizard-sec" />
                <ChipRow label="Preferred Stages" values={STAGES} selected={prefs.preferred_stages} onToggle={(v) => toggle("preferred_stages", v)} keyPrefix="investor-wizard-stg" />
                <ChipRow label="Preferred Geography" values={GEOS} selected={prefs.preferred_geography} onToggle={(v) => toggle("preferred_geography", v)} keyPrefix="investor-wizard-geo" />
              </div>
            </div>
          )}

          {step === "ticket" && (
            <div data-testid="investor-wizard-step-ticket">
              <div className="flex items-center gap-2 mb-3 text-emerald-700">
                <Wallet size={16} />
                <div className="overline">Ticket</div>
              </div>
              <div className="font-heading text-xl text-ink mb-4">Ticket range & risk appetite.</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Ticket Size Min (₹)" type="number" value={prefs.ticket_size_min} onChange={(e) => setPrefs((p) => ({ ...p, ticket_size_min: e.target.value }))} testId="investor-wizard-ticket-min" placeholder="2500000" />
                <Field label="Ticket Size Max (₹)" type="number" value={prefs.ticket_size_max} onChange={(e) => setPrefs((p) => ({ ...p, ticket_size_max: e.target.value }))} testId="investor-wizard-ticket-max" placeholder="25000000" />
                <div className="md:col-span-2">
                  <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Risk Appetite</div>
                  <div className="flex flex-wrap gap-2">
                    {["Conservative", "Balanced", "Aggressive"].map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setPrefs((p) => ({ ...p, risk_appetite: r }))}
                        className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border rounded transition-colors ${
                          prefs.risk_appetite === r ? "border-emerald-600 text-emerald-700 bg-emerald-50" : "border-navy/15 text-ink-muted hover:border-emerald-300"
                        }`}
                        data-testid={`investor-wizard-risk-${r}`}
                      >{r}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === "done" && (
            <div className="text-center py-4" data-testid="investor-wizard-step-done">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle2 size={32} className="text-emerald-600" />
              </div>
              <div className="font-heading text-2xl text-ink mb-2">You{"\u2019"}re all set!</div>
              <p className="text-ink-soft max-w-md mx-auto">
                Your thesis is saved. Head to the marketplace to see curated matches — or complete KYC first
                to unlock deal rooms.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t border-navy/10 bg-navy/[0.02] flex items-center justify-between">
          <button
            onClick={dismiss}
            className="text-sm text-ink-muted hover:text-ink transition-colors"
            data-testid="investor-wizard-skip-footer"
          >
            Skip for now
          </button>
          <div className="flex items-center gap-2">
            {idx > 0 && !isLast && (
              <button
                onClick={back}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-navy/15 text-ink text-sm hover:bg-navy/[0.04] transition-colors"
                data-testid="investor-wizard-back"
              >
                <ArrowLeft size={14} /> Back
              </button>
            )}
            <button
              onClick={next}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-heading transition-colors"
              data-testid="investor-wizard-next"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {isLast ? "Go to dashboard" : idx === 0 ? "Let's go" : "Continue"}
              {!isLast && <ArrowRight size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId, placeholder, type = "text" }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">{label}</div>
      <input
        type={type}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 rounded-md border border-navy/15 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none text-ink"
        data-testid={testId}
      />
    </div>
  );
}

function ChipRow({ label, values, selected, onToggle, keyPrefix }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-2">
        {values.map((v) => {
          const on = (selected || []).includes(v);
          return (
            <button
              type="button"
              key={v}
              onClick={() => onToggle(v)}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border rounded transition-colors ${
                on ? "border-emerald-600 text-emerald-700 bg-emerald-50" : "border-navy/15 text-ink-muted hover:border-emerald-300"
              }`}
              data-testid={`${keyPrefix}-${v}`}
            >{v}</button>
          );
        })}
      </div>
    </div>
  );
}
